import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { css } from '@emotion/core'
import { string, bool, shape } from 'prop-types'
import { Grid, Cell, Card, useContainerWidth, DialogOverlay, useOverlay, FlowGrid, H3, Separator } from '@fs/zion-ui'
import { NoticeLoading } from '@fs/zion-icon'
import axios from '@fs/zion-axios'
import { useTranslation } from 'react-i18next'
import { MemoryUploader } from '@fs/zion-memories'
import { useUser } from '@fs/zion-user'
import filterMemories from '../../../../../helpers/memory_helper'
import useMemories from '../../../../../hooks/useMemories'
import { usePersonViewerState } from '../../../state/PersonViewerStore'
import useDSApplicationMode from '../../../../../hooks/useDSApplicationMode'
import MemoriesImageViewer from './MemoriesImageViewer/MemoriesImageViewer'

// Card renders a native <button>, so Enter and Space already arrive as clicks
const ImageCard = React.memo(function ImageCard({ image, index, label, onOpen, spacerStyle }) {
  if (!image?.thumbnail) return null

  return (
    <Card
      data-index={index}
      aria-label={label}
      onClick={(event) => onOpen(index, event)}
      linkName="PersonPhotos:PHOTO_CARD:CLICK"
      role="button"
      tabIndex={0}
      backgroundImage={image.thumbnail}
      edges="none"
    >
      <div style={spacerStyle} />
    </Card>
  )
})

const photoGridContainerCSS = css`
  margin: 20px;
`

const gridContainerCSS = css`
  margin: 20px;
`

const storyTitleCardCSS = css`
  font-weight: bold;
  padding: 10px;
`

const documentTitleCSS = css`
  font-weight: bold;
  margin-bottom: 10px;
  padding: 5px;
`

const audioTitleCSS = css`
  font-weight: bold;
  padding: 10px;
`

const cssImage = css`
  width: 100%;
  height: 275px;
  outline: none;
  object-fit: cover;
  object-position: center top;
`

const imageFrameCSS = css`
  width: 100%;
  height: 70vh;
`

const audioPlayerCSS = css`
  width: 100%;
`

export default function PersonMemories({ personId, isAccessible, treePerson }) {
  const atWidth = useContainerWidth()
  const [memoriesState, memoriesDispatch] = useMemories({ personId })
  const [isLoading, setIsLoading] = useState(true)
  const personViewerState = usePersonViewerState()
  const { lockMemories } = personViewerState
  const [t] = useTranslation()
  const appMode = useDSApplicationMode()
  const user = useUser()
  const { signedIn } = user
  const canUpload = signedIn && !lockMemories && appMode === 'WEB'

  const refreshMemories = useCallback(() => {
    memoriesDispatch({ type: 'load' })
  }, [memoriesDispatch])

  // Data States
  const [photos, setPhotos] = useState([])
  const [stories, setStories] = useState([])
  const [documents, setDocuments] = useState([])
  const [audioList, setAudioList] = useState([])

  // Photo Viewer State
  const photoOverlay = useOverlay()
  const [imageIndex, setImageIndex] = useState(0)

  const imageCardSpacerStyle = useMemo(() => {
    return atWidth({
      default: { height: '150px' },
      md: { height: '250px' },
    })
  }, [atWidth])

  // Story State
  const storyOverlay = useOverlay()
  const [currentStoryTitle, setCurrentStoryTitle] = useState('')
  const [currentStoryText, setCurrentStoryText] = useState('')

  // Document State
  const documentOverlay = useOverlay()
  const [selectedDocumentURL, setSelectedDocumentURL] = useState('')
  const [selectedDocumentTitle, setSelectedDocumentTitle] = useState('')

  useEffect(() => {
    if (memoriesState.complete && memoriesState.res?.sourceDescriptions) {
      setPhotos(filterMemories({ memories: memoriesState.res, memoryType: 'image' }))
      setStories(filterMemories({ memories: memoriesState.res, memoryType: 'text/plain' }))
      setDocuments(filterMemories({ memories: memoriesState.res, memoryType: 'application/pdf' }))
      setAudioList(filterMemories({ memories: memoriesState.res, memoryType: 'audio' }))
      setIsLoading(false)
    } else if (memoriesState.complete) {
      setIsLoading(false)
    }
  }, [memoriesState])

  // --- Photo Handlers ---
  const { open: openPhotoOverlay } = photoOverlay
  const openGallery = useCallback(
    (index, event) => {
      setImageIndex(index)
      // The target is where focus goes back to when the viewer closes
      openPhotoOverlay({ target: event?.currentTarget })
    },
    [openPhotoOverlay]
  )

  // --- Story Handlers ---
  const openStoryDialog = (title, link) => {
    axios.get(link).then(({ data }) => {
      setCurrentStoryTitle(title)
      const formattedData = (data ?? '').replaceAll('_____', '-----')
      setCurrentStoryText(formattedData)
      storyOverlay.open()
    })
  }

  // --- Document Handlers ---
  const openDocumentDialog = ({ title, link }) => {
    setSelectedDocumentURL(link)
    setSelectedDocumentTitle(title)
    documentOverlay.open()
  }

  if (isLoading) {
    return (
      <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '500px' }}>
        <NoticeLoading size="lg" />
      </div>
    )
  }

  // Calculate if we have any content at all
  const hasContent = photos.length > 0 || stories.length > 0 || documents.length > 0 || audioList.length > 0

  if (!hasContent) {
    return (
      <div css={{ textAlign: 'center', marginTop: atWidth({ default: '75px', xxl: '200px' }) }}>
        <H3>{t('person-details.no-records', 'No records found')}</H3>
        {canUpload && (
          <>
            <Separator />
            <H3>{t('person-details.be-first', 'Be the first to add to this record')}</H3>
            <Separator />
            <MemoryUploader treePerson={treePerson || { id: personId, personId }} onEditSuccess={refreshMemories} />
          </>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* Photos Section */}
      {photos.length > 0 && (
        <div css={photoGridContainerCSS}>
          <FlowGrid columnCount={atWidth({ default: 1, xs: 2, sm: 2, md: 3, lg: 4, xxl: 6 })}>
            {photos.map((image, index) => (
              <ImageCard
                image={image}
                index={index}
                label={t('person-details.open-photo', 'Open photo {number} of {total}', {
                  number: index + 1,
                  total: photos.length,
                })}
                onOpen={openGallery}
                spacerStyle={imageCardSpacerStyle}
                key={image.id ?? image.thumbnail ?? index}
              />
            ))}
          </FlowGrid>
        </div>
      )}

      {/* Stories Section */}
      {stories.length > 0 && (
        <div css={gridContainerCSS}>
          <Grid>
            {stories.map((story) => (
              <Cell columns={atWidth({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })} key={story.link}>
                <Card
                  onClick={() => openStoryDialog(story.title, story.link)}
                  role="button"
                  tabIndex={-1}
                  onKeyPress={() => openStoryDialog(story.title, story.link)}
                  edges="elevation"
                >
                  <div css={storyTitleCardCSS}>{story.title}</div>
                  <div css={{ padding: '10px' }}>{story.description}</div>
                </Card>
              </Cell>
            ))}
          </Grid>
        </div>
      )}

      {/* Documents Section */}
      {documents.length > 0 && (
        <div css={gridContainerCSS}>
          <Grid>
            {documents.map((item) => (
              <Cell columns={atWidth({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })} key={item.id}>
                <Card
                  onKeyPress={() => openDocumentDialog({ title: item.title, link: item.link })}
                  onClick={() => openDocumentDialog({ title: item.title, link: item.link })}
                  role="button"
                  tabIndex={-1}
                  data-link={item.link}
                  edges="elevation"
                >
                  <span css={documentTitleCSS}>{item.title}</span>
                  <img css={cssImage} src={item.thumb} loading="lazy" alt=" " />
                </Card>
              </Cell>
            ))}
          </Grid>
        </div>
      )}

      {/* Audio Section */}
      {audioList.length > 0 && (
        <div css={gridContainerCSS}>
          <Grid>
            {audioList.map((memory) => (
              <Cell columns={atWidth({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })} key={memory.link}>
                <Card edges="elevation">
                  <div css={audioTitleCSS}>{memory.title}</div>
                  <audio css={audioPlayerCSS} key={memory.link} controls src={memory.link}>
                    <track default="" kind="captions" />
                  </audio>
                </Card>
              </Cell>
            ))}
          </Grid>
        </div>
      )}

      {/* Overlays */}
      <MemoriesImageViewer
        overlay={photoOverlay}
        photos={photos}
        focusedIndex={imageIndex}
        setFocusedIndex={setImageIndex}
        isAccessible={isAccessible}
      />

      <DialogOverlay title={currentStoryTitle} {...storyOverlay}>
        <div>{currentStoryText}</div>
      </DialogOverlay>

      <DialogOverlay title={selectedDocumentTitle} {...documentOverlay} size="xl">
        <iframe id="pdf-frame" title="View PDF" css={imageFrameCSS} src={selectedDocumentURL} width="100%" />
      </DialogOverlay>

      {/* Memory Uploader */}
      {canUpload && (
        <div css={gridContainerCSS}>
          <Separator />
          <MemoryUploader treePerson={treePerson || { id: personId, personId }} onEditSuccess={refreshMemories} />
        </div>
      )}
    </div>
  )
}

PersonMemories.propTypes = {
  personId: string,
  isAccessible: bool,
  treePerson: shape({}),
}
